import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const HEARTBEAT_INTERVAL_MS = 30_000; // 30 seconds
const STALE_THRESHOLD_DESCRIPTION = "If no heartbeat for >2 min, sweep marks offline";

interface UsePresenceHeartbeatOptions {
  doctorId: string;
  currentStatus: "online" | "busy" | "offline";
  onStatusChange: (status: "online" | "busy" | "offline") => void;
}

/**
 * Messenger-style presence detection for doctors.
 * - Sends heartbeat every 30s while tab is visible and status is online/busy
 * - Sets offline immediately on tab close (beforeunload)
 * - Sets offline when tab becomes hidden for extended period
 * - Restores previous status when tab becomes visible again
 */
export function usePresenceHeartbeat({
  doctorId,
  currentStatus,
  onStatusChange,
}: UsePresenceHeartbeatOptions) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusBeforeHiddenRef = useRef<"online" | "busy" | "offline">(currentStatus);
  const hiddenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep ref in sync with prop
  useEffect(() => {
    statusBeforeHiddenRef.current = currentStatus;
  }, [currentStatus]);

  const sendHeartbeat = useCallback(async () => {
    if (currentStatus === "offline") return;

    try {
      const now = new Date().toISOString();
      await supabase
        .from("doctors")
        .update({ last_online_at: now, last_active_at: now } as any)
        .eq("id", doctorId);
    } catch (err) {
      console.error("Heartbeat failed:", err);
    }
  }, [doctorId, currentStatus]);

  const goOffline = useCallback(async () => {
    try {
      await supabase
        .from("doctors")
        .update({
          availability_status: "offline",
          last_online_at: new Date().toISOString(),
        })
        .eq("id", doctorId);
      onStatusChange("offline");
    } catch (err) {
      console.error("Failed to set offline:", err);
    }
  }, [doctorId, onStatusChange]);

  const restoreOnline = useCallback(async () => {
    const previousStatus = statusBeforeHiddenRef.current;
    if (previousStatus === "offline") return;

    try {
      await supabase
        .from("doctors")
        .update({
          availability_status: previousStatus,
          last_online_at: new Date().toISOString(),
        })
        .eq("id", doctorId);
      onStatusChange(previousStatus);
    } catch (err) {
      console.error("Failed to restore status:", err);
    }
  }, [doctorId, onStatusChange]);

  // Heartbeat interval
  useEffect(() => {
    if (currentStatus === "offline") {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    // Send initial heartbeat
    sendHeartbeat();

    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [currentStatus, sendHeartbeat]);

  // Visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden — start a 2-minute timer before going offline
        // This avoids flicker when user briefly switches tabs
        if (currentStatus !== "offline") {
          statusBeforeHiddenRef.current = currentStatus;
          hiddenTimerRef.current = setTimeout(() => {
            goOffline();
          }, 120_000); // 2 minutes grace period
        }
      } else {
        // Tab visible again — cancel offline timer and restore
        if (hiddenTimerRef.current) {
          clearTimeout(hiddenTimerRef.current);
          hiddenTimerRef.current = null;
        }
        // If we were set offline by the timer, restore
        if (statusBeforeHiddenRef.current !== "offline") {
          restoreOnline();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (hiddenTimerRef.current) clearTimeout(hiddenTimerRef.current);
    };
  }, [currentStatus, goOffline, restoreOnline]);

  // beforeunload — immediate offline on browser close
  useEffect(() => {
    if (currentStatus === "offline") return;

    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery during page unload
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/doctors?id=eq.${doctorId}`;
      const body = JSON.stringify({
        availability_status: "offline",
        last_online_at: new Date().toISOString(),
      });

      navigator.sendBeacon(
        url,
        new Blob([body], { type: "application/json" })
      );
      // Note: sendBeacon with REST API needs auth header which Blob doesn't support.
      // Fallback: the server-side sweep will catch this within 2 minutes.
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [doctorId, currentStatus]);
}
